/**
 * The turbine — M6's engine, built now.
 *
 * ```
 *   saw + saw(×1.5) ─► bandpass(f ← N1) ─► whine gain ─────────────┐
 *   white ─► lowpass(f ← N1) ─► roar gain(← N1³) ──────────────────┼─► out
 *   white ─► lowpass(180) ─► afterburner rumble ───────────────────┘
 * ```
 *
 * ## Why it exists in the audio milestone
 *
 * The brief asks for "hooks for M6", and a hook that is an empty function is not a hook — it is a
 * note saying somebody should write one. The fighter's flight model does not exist yet, so this
 * voice is driven by an explicit N1 and nozzle stage rather than by a sim state, and those come
 * from the debug triggers today and from `sim/propulsion.ts`'s turbine when M6 builds it. The
 * synth itself is finished: it spools, it whines, and it lights.
 *
 * ## The three layers
 *
 * **Whine.** Fan blades passing the inlet guide vanes: a hard, narrow, high tone that rises with
 * N1 and is the single most recognisable thing about a jet. Two sawtooths a fifth apart through a
 * tracking bandpass, which gives the metallic buzz that a single tone cannot.
 *
 * **Roar.** Combustion and exhaust mixing — broadband, absent at idle, overwhelming at power.
 * {@link turbineRoarGain} cubes it, so the top of the throttle range is where the drama is.
 *
 * **Reheat.** A separate low-frequency layer that arrives as an event rather than a fade,
 * because that is what lighting the burner is.
 *
 * `updateFrame(alpha)`: {@link TurbineVoice.updateFrame}, once per rendered frame, with the real
 * frame delta so the spool lag runs in wall-clock time. Allocation-free.
 */

import type { AudioBank } from '../bank.js';
import { afterburnerGain, spoolToward, turbineRoarGain, turbineWhineHz } from '../model.js';
import { setTarget } from '../param.js';
import { bandpass, disconnectAll, gainNode, loopingSource, lowpass } from './parts.js';

export class TurbineVoice {
  private readonly context: AudioContext;

  private readonly whineA: OscillatorNode;
  private readonly whineB: OscillatorNode;
  private readonly whineBand: BiquadFilterNode;
  private readonly whineLevel: GainNode;

  private readonly roarSource: AudioBufferSourceNode;
  private readonly roarLow: BiquadFilterNode;
  private readonly roarLevel: GainNode;

  private readonly reheatSource: AudioBufferSourceNode;
  private readonly reheatLow: BiquadFilterNode;
  private readonly reheatLevel: GainNode;

  private readonly output: GainNode;

  /** Spool state, 0…1. Lags the commanded value; see {@link spoolToward}. */
  private n1 = 0;

  constructor(context: AudioContext, bank: AudioBank, destination: AudioNode) {
    this.context = context;

    this.output = gainNode(context, 0);
    this.output.connect(destination);

    this.whineLevel = gainNode(context, 0);
    this.whineLevel.connect(this.output);
    this.whineBand = bandpass(context, 2_500, 5);
    this.whineBand.connect(this.whineLevel);
    this.whineA = context.createOscillator();
    this.whineA.type = 'sawtooth';
    this.whineA.frequency.value = 400;
    this.whineB = context.createOscillator();
    this.whineB.type = 'sawtooth';
    this.whineB.frequency.value = 600;
    const whineMix = gainNode(context, 0.4);
    this.whineA.connect(whineMix);
    this.whineB.connect(whineMix);
    whineMix.connect(this.whineBand);
    this.whineA.start(0);
    this.whineB.start(0);

    this.roarLevel = gainNode(context, 0);
    this.roarLevel.connect(this.output);
    this.roarLow = lowpass(context, 1_200, 0.8);
    this.roarLow.connect(this.roarLevel);
    this.roarSource = loopingSource(context, bank.white, bank.whiteLoopEndS, 1, 0.6);
    this.roarSource.connect(this.roarLow);

    this.reheatLevel = gainNode(context, 0);
    this.reheatLevel.connect(this.output);
    this.reheatLow = lowpass(context, 180, 1.4);
    this.reheatLow.connect(this.reheatLevel);
    this.reheatSource = loopingSource(context, bank.white, bank.whiteLoopEndS, 0.71, 2.1);
    this.reheatSource.connect(this.reheatLow);
  }

  /** The spool state, 0…1, for the tests and the debug overlay. */
  get spool(): number {
    return this.n1;
  }

  /**
   * @param commandedN1  Where the throttle is asking the fan to be, 0…1.
   * @param nozzleStage  Afterburner stage, 0 (dry) … 1 (max reheat).
   * @param active       False when no turbine-powered aircraft is loaded; silences the voice.
   */
  updateFrame(
    commandedN1: number,
    nozzleStage: number,
    frameDtS: number,
    dopplerRatio: number,
    active: boolean,
  ): void {
    const now = this.context.currentTime;
    this.n1 = spoolToward(this.n1, active ? commandedN1 : 0, frameDtS);

    const whineHz = turbineWhineHz(this.n1) * dopplerRatio;
    setTarget(this.whineA.frequency, whineHz * 0.5, 0.05, now);
    setTarget(this.whineB.frequency, whineHz * 0.75, 0.05, now);
    setTarget(this.whineBand.frequency, whineHz, 0.05, now);
    setTarget(this.whineLevel.gain, (0.06 + 0.2 * this.n1) * 0.6, 0.06, now);

    setTarget(this.roarLow.frequency, (500 + 1_800 * this.n1) * dopplerRatio, 0.08, now);
    setTarget(this.roarLevel.gain, turbineRoarGain(this.n1) * 0.7, 0.08, now);

    setTarget(this.reheatLevel.gain, afterburnerGain(active ? nozzleStage : 0) * 0.55, 0.09, now);

    setTarget(this.output.gain, active ? 1 : 0, 0.15, now);
  }

  dispose(): void {
    this.whineA.stop();
    this.whineB.stop();
    this.roarSource.stop();
    this.reheatSource.stop();
    disconnectAll([
      this.whineA,
      this.whineB,
      this.whineBand,
      this.whineLevel,
      this.roarSource,
      this.roarLow,
      this.roarLevel,
      this.reheatSource,
      this.reheatLow,
      this.reheatLevel,
      this.output,
    ]);
  }
}
